import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { GoldenCircles, GridDots, CornerMark } from "./GeometricDecor";
import { services, type PriceSuffix } from "@/lib/content";
import { convertHufToRoundedEur, formatEur, formatHuf, useEurRate } from "@/lib/currency";
import { getLanguageFromPath } from "@/lib/language";

function formatPrice(huf: number, suffix: PriceSuffix | undefined, language: "hu" | "en", rate: number, copy: typeof services.hu) {
  const base = language === "hu" ? formatHuf(huf) : formatEur(convertHufToRoundedEur(huf, rate));
  if (!suffix || suffix === "none") return base;
  if (suffix === "from") return language === "hu" ? `${base}${copy.from}` : `${copy.from} ${base}`;
  if (suffix === "perImage") return `${base} ${copy.perImage}`;
  if (suffix === "perHour") return `${base} ${copy.perHour}`;
  return base;
}

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [openCategory, setOpenCategory] = useState<number | null>(0);
  const [beautyOpen, setBeautyOpen] = useState(false);
  const location = useLocation();
  const language = getLanguageFromPath(location.pathname);
  const copy = services[language];
  const { rate } = useEurRate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} aria-label={copy.aria} itemScope itemType="https://schema.org/Service" className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundColor: "#004643" }}>
      {/* Geometric decor */}
      <GoldenCircles size={440} opacity={0.05} color="#acd1c6" className="-top-24 -left-24" />
      <GoldenCircles size={260} opacity={0.04} color="#acd1c6" className="bottom-20 right-[-60px]" />
      <GridDots cols={8} rows={5} gap={30} opacity={0.07} color="#acd1c6" className="right-12 top-20" />
      <CornerMark position="tl" size={28} color="#acd1c6" opacity={0.2} className="top-10 left-10" />
      <CornerMark position="br" size={28} color="#acd1c6" opacity={0.2} className="bottom-10 right-10" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="text-center mb-16">
          <div
            className="reveal opacity-0 translate-y-6 transition-all duration-700 inline-flex items-center gap-2 px-5 py-2 rounded-full border text-xs font-medium tracking-widest uppercase mb-6"
            style={{ borderColor: "rgba(172,209,198,0.3)", color: "#acd1c6" }}
          >
            <i className="ri-service-line text-sm" />
            {copy.eyebrow}
          </div>
          <h2
            className="reveal opacity-0 translate-y-6 transition-all duration-700 text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight mb-4"
            style={{ fontFamily: "'League Spartan', sans-serif" }}
            itemProp="name"
          >
            {copy.title}
          </h2>
          <p className="reveal opacity-0 translate-y-6 transition-all duration-700 text-white/50 text-base max-w-xl mx-auto" itemProp="description">
            {copy.intro}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex flex-col sm:flex-row flex-wrap gap-3 justify-center mb-10">
          {copy.categories.map((cat, idx) => (
            <button
              key={cat.title}
              onClick={() => { setOpenCategory(idx === openCategory ? null : idx); setBeautyOpen(false); }}
              className="flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={
                openCategory === idx
                  ? { backgroundColor: "#acd1c6", color: "#004643" }
                  : { border: "1px solid rgba(172,209,198,0.3)", color: "rgba(172,209,198,0.7)" }
              }
              onMouseEnter={(e) => {
                if (openCategory !== idx) {
                  (e.currentTarget as HTMLElement).style.borderColor = "rgba(172,209,198,0.6)";
                  (e.currentTarget as HTMLElement).style.color = "#acd1c6";
                }
              }}
              onMouseLeave={(e) => {
                if (openCategory !== idx) {
                  (e.currentTarget as HTMLElement).style.borderColor = "rgba(172,209,198,0.3)";
                  (e.currentTarget as HTMLElement).style.color = "rgba(172,209,198,0.7)";
                }
              }}
            >
              <i className={`${cat.icon} text-base`} />
              {cat.title}
            </button>
          ))}
          {/* Beauty tab */}
          <button
            onClick={() => { setBeautyOpen(!beautyOpen); setOpenCategory(null); }}
            className="flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={
              beautyOpen
                ? { backgroundColor: "#acd1c6", color: "#004643" }
                : { border: "1px solid rgba(172,209,198,0.3)", color: "rgba(172,209,198,0.7)" }
            }
            onMouseEnter={(e) => {
              if (!beautyOpen) {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(172,209,198,0.6)";
                (e.currentTarget as HTMLElement).style.color = "#acd1c6";
              }
            }}
            onMouseLeave={(e) => {
              if (!beautyOpen) {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(172,209,198,0.3)";
                (e.currentTarget as HTMLElement).style.color = "rgba(172,209,198,0.7)";
              }
            }}
          >
            <i className="ri-scissors-cut-line text-base" />
            {copy.beautyTab}
          </button>
        </div>

        {/* Service Pricing Table */}
        {copy.categories.map((cat, idx) => (
          <div
            key={cat.title}
            className={`reveal opacity-0 translate-y-6 transition-all duration-700 mb-5 overflow-hidden rounded-2xl ${openCategory === idx ? "block" : "hidden"}`}
            style={{ border: "1px solid rgba(172,209,198,0.2)" }}
          >
            {/* Category Header */}
            <div className="px-6 py-5 flex items-center gap-4" style={{ backgroundColor: "rgba(172,209,198,0.08)", borderBottom: "1px solid rgba(172,209,198,0.15)" }}>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: "rgba(172,209,198,0.15)" }}>
                <i className={`${cat.icon} text-lg`} style={{ color: "#acd1c6" }} />
              </div>
              <div>
                <div className="text-white font-semibold text-base">{cat.title}</div>
                <div className="text-white/40 text-xs mt-0.5">{cat.desc}</div>
              </div>
            </div>

            {/* Items */}
            <div>
              {cat.items.map((item) => (
                <div
                  key={item.name}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-5 transition-colors duration-200"
                  style={
                    item.highlight
                      ? { backgroundColor: "rgba(172,209,198,0.1)", borderTop: "1px solid rgba(172,209,198,0.1)" }
                      : { borderTop: "1px solid rgba(172,209,198,0.06)" }
                  }
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium text-sm">{item.name}</span>
                      {item.highlight && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(172,209,198,0.2)", color: "#acd1c6" }}>
                          {copy.best}
                        </span>
                      )}
                      {item.colorBtn && (
                        <button
                          onClick={() => document.querySelector("#colors")?.scrollIntoView({ behavior: "smooth" })}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                          style={{ backgroundColor: "rgba(172,209,198,0.15)", color: "#acd1c6", border: "1px solid rgba(172,209,198,0.3)" }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(172,209,198,0.25)"; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(172,209,198,0.15)"; }}
                        >
                          <i className="ri-palette-line text-xs" />
                          {copy.colorCta}
                        </button>
                      )}
                    </div>
                    <div className="text-white/40 text-xs mt-1">{item.detail}</div>
                  </div>
                  <div className="shrink-0">
                    <span
                      className="text-base font-bold whitespace-nowrap"
                      style={{ fontFamily: "'League Spartan', sans-serif", color: item.highlight ? "#acd1c6" : "white" }}
                    >
                      {formatPrice(item.huf, item.suffix, language, rate, copy)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Beauty Package Block */}
        {beautyOpen && (
          <div
            className="mb-5 overflow-hidden rounded-2xl transition-all duration-700"
            style={{ opacity: 1, transform: "translateY(0)", border: "1px solid rgba(172,209,198,0.2)" }}
          >
            {/* Header */}
            <div className="px-6 py-5 flex items-center gap-4" style={{ backgroundColor: "rgba(172,209,198,0.08)", borderBottom: "1px solid rgba(172,209,198,0.15)" }}>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: "rgba(172,209,198,0.15)" }}>
                <i className="ri-scissors-cut-line text-lg" style={{ color: "#acd1c6" }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-white font-semibold text-base">{copy.beautyTitle}</div>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(172,209,198,0.2)", color: "#acd1c6" }}>
                    {copy.quoteLabel}
                  </span>
                </div>
                <div className="text-white/40 text-xs mt-0.5">{copy.beautyDesc}</div>
              </div>
            </div>

            {/* Items */}
            <div>
              {copy.beautyItems.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center justify-between gap-4 px-6 py-4"
                  style={{ borderTop: "1px solid rgba(172,209,198,0.06)" }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                      style={{ backgroundColor: item.required ? "rgba(172,209,198,0.2)" : "rgba(172,209,198,0.07)", border: item.required ? "none" : "1px solid rgba(172,209,198,0.25)" }}
                    >
                      <i
                        className={item.required ? "ri-check-line text-xs" : "ri-add-line text-xs"}
                        style={{ color: "#acd1c6" }}
                      />
                    </div>
                    <span className="text-white font-medium text-sm">{item.name}</span>
                  </div>
                  <span
                    className="text-xs px-3 py-1 rounded-full whitespace-nowrap"
                    style={
                      item.required
                        ? { backgroundColor: "rgba(172,209,198,0.15)", color: "#acd1c6" }
                        : { border: "1px solid rgba(172,209,198,0.2)", color: "rgba(172,209,198,0.5)" }
                    }
                  >
                    {item.required ? copy.required : copy.optional}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer CTA */}
            <div
              className="px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              style={{ backgroundColor: "rgba(172,209,198,0.06)", borderTop: "1px solid rgba(172,209,198,0.12)" }}
            >
              <div>
                <div className="text-white/60 text-xs mb-1">{copy.beautyFooter}</div>
                <div className="text-white/40 text-xs">{copy.beautyRequired}</div>
              </div>
              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 cursor-pointer whitespace-nowrap shrink-0"
                style={{ backgroundColor: "#acd1c6", color: "#004643" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#9bc4b8"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#acd1c6"; }}
              >
                {copy.requestQuote}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        )}

        {/* Discounts & Conditions */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 grid grid-cols-1 md:grid-cols-2 gap-5 mt-10 mb-20">
          {/* Discounts */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: "rgba(172,209,198,0.08)", border: "1px solid rgba(172,209,198,0.2)" }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(172,209,198,0.15)" }}>
                <i className="ri-percent-line text-sm" style={{ color: "#acd1c6" }} />
              </div>
              <span className="text-white font-semibold text-sm">{copy.discountTitle}</span>
            </div>
            <div className="flex flex-col gap-3">
              {copy.discounts.map((d) => (
                <div key={d.range} className="flex items-start justify-between gap-4">
                  <span className="text-white/50 text-xs leading-relaxed">{d.range}</span>
                  <span className="font-bold text-sm whitespace-nowrap shrink-0" style={{ color: "#acd1c6" }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Conditions */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: "rgba(172,209,198,0.08)", border: "1px solid rgba(172,209,198,0.2)" }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(172,209,198,0.15)" }}>
                <i className="ri-information-line text-sm" style={{ color: "#acd1c6" }} />
              </div>
              <span className="text-white font-semibold text-sm">{copy.conditionsTitle}</span>
            </div>
            <div className="flex flex-col gap-3">
              {copy.conditions.map((c) => (
                <div key={c.text} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i className={`${c.icon} text-sm`} style={{ color: "rgba(172,209,198,0.5)" }} />
                  </div>
                  <span className="text-white/50 text-xs leading-relaxed">{c.text}</span>
                </div>
              ))}
              <div className="mt-2 pt-3 text-white/30 text-xs" style={{ borderTop: "1px solid rgba(172,209,198,0.1)" }}>
                {copy.taxNote}
              </div>
            </div>
          </div>
        </div>

        {/* Process */}
        <div style={{ borderTop: "1px solid rgba(172,209,198,0.15)" }} className="pt-16">
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-12">
            <h3
              className="text-2xl md:text-3xl font-light text-white mb-3"
              style={{ fontFamily: "'League Spartan', sans-serif" }}
            >
              {copy.processTitle}
            </h3>
            <p className="text-white/40 text-sm">{copy.processIntro}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {copy.process.map((step, idx) => (
              <div key={step.num} className="reveal opacity-0 translate-y-6 transition-all duration-700 relative">
                {idx < copy.process.length - 1 && (
                  <div className="hidden lg:block absolute top-6 left-full h-px z-0" style={{ width: "calc(100% - 2rem)", backgroundColor: "rgba(172,209,198,0.15)" }} />
                )}
                <div className="relative z-10">
                  <div className="text-4xl font-bold mb-3" style={{ fontFamily: "'League Spartan', sans-serif", color: "rgba(172,209,198,0.15)" }}>
                    {step.num}
                  </div>
                  <div className="text-white font-semibold text-sm mb-2">{step.title}</div>
                  <div className="text-white/40 text-xs leading-relaxed">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mt-16">
          <button
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-semibold transition-colors duration-300 cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#acd1c6", color: "#004643" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#9bc4b8"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#acd1c6"; }}
          >
            {copy.bottomCta}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

      </div>
    </section>
  );
}
